import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Zap } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";

const MAX_POLLS = 8;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | pending | error
  const [info, setInfo] = useState(null);
  const polls = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }

    let timer;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setInfo(res.data);
          setState("success");
          await refreshUser();
          return;
        }
        if (["failed", "expired"].includes(res.data.payment_status)) { setState("error"); return; }
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        polls.current += 1;
        if (polls.current >= MAX_POLLS) { setState("error"); return; }
        timer = setTimeout(poll, 2000);
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, refreshUser]);

  return (
    <div className="min-h-screen flex items-center justify-center grain px-4">
      <div className="bg-white border border-slate-200 rounded-3xl paper-shadow p-10 max-w-md w-full text-center" data-testid="payment-success-page">
        {state === "checking" && (
          <>
            <Loader2 className="h-12 w-12 text-indigo-600 animate-spin mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-5">Confirming your payment…</h1>
            <p className="text-slate-500 mt-2">This only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle2 className="h-9 w-9 text-emerald-600" />
            </div>
            <h1 className="font-display text-2xl font-bold mt-5">Payment successful!</h1>
            <p className="text-slate-600 mt-2 flex items-center justify-center gap-1.5">
              <Zap className="h-4 w-4 text-amber-500 fill-amber-500" />
              <span className="font-mono font-semibold">{info?.credits_added} credits</span> added to your account.
            </p>
            <div className="mt-6 flex gap-3 justify-center">
              <Link to="/studio"><Button data-testid="success-go-studio" className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">Create a PDF</Button></Link>
              <Link to="/documents"><Button variant="outline" className="rounded-full">My Documents</Button></Link>
            </div>
          </>
        )}
        {state === "pending" && (
          <>
            <Loader2 className="h-12 w-12 text-amber-500 mx-auto" />
            <h1 className="font-display text-2xl font-bold mt-5">Still processing</h1>
            <p className="text-slate-500 mt-2">Your payment is taking longer than usual. Your credits will appear shortly.</p>
            <Button onClick={() => navigate("/studio")} className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">Back to Studio</Button>
          </>
        )}
        {state === "error" && (
          <>
            <div className="h-16 w-16 rounded-full bg-red-100 flex items-center justify-center mx-auto">
              <XCircle className="h-9 w-9 text-red-600" />
            </div>
            <h1 className="font-display text-2xl font-bold mt-5">Something went wrong</h1>
            <p className="text-slate-500 mt-2">We couldn't confirm your payment. If you were charged, credits will be added automatically.</p>
            <Link to="/buy-credits"><Button className="mt-6 bg-indigo-600 hover:bg-indigo-700 text-white rounded-full">Try again</Button></Link>
          </>
        )}
      </div>
    </div>
  );
}
